import type { Audience, DocumentSection, DocumentSectionContent, Geography, Proposal } from '@shared/types';

/**
 * Everything a page can change, funnelled through typed mutations.
 * Presentation copy -> set_section_content. Plan numbers -> plan mutations.
 * Nothing here ever writes a number into copy. Superset of the V2/V3
 * BlockActions with page visibility added for the V5 grid/gallery model.
 */
export interface PageActions {
  saveContent: (
    section: DocumentSection,
    patch: Partial<DocumentSectionContent>,
  ) => void;
  saveTitle: (section: DocumentSection, title: string) => void;
  setChannelBudget: (productId: string, monthlyBudget: number, label: string) => void;
  setMonthlyBudget: (monthly: number) => void;
  /** Finalization: adopts a tier's channels + budget as the working plan. */
  selectTier: (tierId: string, label: string) => void;
  /** Page management: hide (ghost, excluded from focus/share/print) or restore. */
  setHidden: (section: DocumentSection, hidden: boolean) => void;
  /** Targeting edits (design.md V8): radius / ZIP overrides, a versioned set_geography mutation. */
  setGeography: (patch: Partial<Geography>, label: string) => void;
  /** Cosmetic audience-card edits. Query fields are preserved by callers. */
  setAudience: (patch: Partial<Audience>, label: string) => void;
  /** V16 rate cards: a rep's retail price for a line the card leaves blank. `retail` is already converted (a fraction for managed models, see priceOverrideRetail). */
  setPriceOverride: (productId: string, retail: number, label: string) => void;
  /** Clears a rep's price override, returning the line to whatever (if anything) the rate card itself prices it at. */
  clearPriceOverride: (productId: string, label: string) => void;
}

export interface PageProps {
  proposal: Proposal;
  section: DocumentSection;
  actions: PageActions;
  /** Grid thumbnails, print and share render with no edit affordances. */
  readOnly: boolean;
}

/** A no-op action set for surfaces that can never write (grid, print and share). */
export const READ_ONLY_ACTIONS: PageActions = {
  saveContent: () => undefined,
  saveTitle: () => undefined,
  setChannelBudget: () => undefined,
  setMonthlyBudget: () => undefined,
  selectTier: () => undefined,
  setHidden: () => undefined,
  setGeography: () => undefined,
  setAudience: () => undefined,
  setPriceOverride: () => undefined,
  clearPriceOverride: () => undefined,
};
